import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

function Section({ title, children }) {
  return (
    <div className="border rounded-lg p-6 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
      <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{title}</h2>
      <div className="space-y-3" style={{ color: '#b8a68f' }}>{children}</div>
    </div>
  );
}

const TOPICS = [
  { id: 'signup', icon: '📝', en: 'Signup', zh: '注册' },
  { id: 'placement', icon: '🌳', en: 'Placement', zh: '安置' },
];

export default function Guides() {
  const { user, loading: authLoading } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isZh = i18n.language === 'zh';
  const [topic, setTopic] = useState('signup');
  const [floatOpen, setFloatOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]);

  if (!user) {
    return null;
  }

  const selectTopic = (id) => {
    setTopic(id);
    setFloatOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen px-4 pt-24 pb-32 relative" style={{ backgroundColor: '#1a3a2a' }}>
    <div className="max-w-3xl mx-auto">
      <style>{`
        @page { size: portrait; }
        @media print {
          header, nav, footer, .no-print { display: none !important; }
          html, body { background: #1a3a2a !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
          .print-page { background: #1a3a2a !important; padding: 0 !important; margin: 0 !important; }
          .print-only { display: flex !important; }
        }
        .print-only { display: none; }
      `}</style>
      <div className="print-page">
      <div className="print-only items-center gap-2 mb-4">
        <img src="/images/wellis-logo.png" alt="Wellis" style={{ height: '48px', width: 'auto' }} />
        <span className="text-headline-md font-bold uppercase tracking-widest" style={{ color: '#c9a961' }}>WELLIS</span>
      </div>
      <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>
        {isZh ? '指南' : 'Guides'}
      </h1>
      <p className="text-body-sm mb-6" style={{ color: '#999' }}>
        {isZh ? '帮助您使用 Wellis 平台的操作指南' : 'Step-by-step help for using the Wellis platform'}
      </p>

      {/* Topic tabs */}
      <div className="no-print flex gap-2 mb-6 border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
        {TOPICS.map((t) => (
          <button
            key={t.id}
            onClick={() => selectTopic(t.id)}
            className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
            style={
              topic === t.id
                ? { color: '#0d2818', backgroundColor: '#c9a961' }
                : { color: '#c9a961', backgroundColor: 'transparent' }
            }
          >
            {t.icon} {isZh ? t.zh : t.en}
          </button>
        ))}
      </div>

      {topic === 'signup' && (
      <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-headline-md font-bold" style={{ color: '#c9a961' }}>
          {isZh ? '如何注册' : 'How to Sign Up'}
        </h2>
        <button
          onClick={() => window.print()}
          className="no-print text-body-sm font-medium px-4 py-2 rounded border hover:opacity-80 transition-opacity"
          style={{ color: '#0d2818', backgroundColor: '#c9a961', borderColor: '#c9a961' }}
        >
          {isZh ? '🖨️ 打印本指南' : '🖨️ Print this guide'}
        </button>
      </div>

      {isZh ? (
        <>
          <Section title="注册前需要准备">
            <p>邮箱地址<strong style={{ color: '#c9a961' }}>或</strong>电话号码（至少提供一项）。</p>
            <p>有效的<strong style={{ color: '#c9a961' }}>推荐代码</strong>——注册必须由现有会员推荐，推荐代码为必填项。如果您是通过邀请链接（?ref=...）打开的注册页面，推荐代码会自动填入。</p>
            <p>您的名字（必填）和姓氏（选填）。</p>
            <p>一个您自选的 <strong style={{ color: '#c9a961' }}>Wellis ID</strong>——必须恰好为 10 位字母数字，且未被使用（页面会自动为您检查是否可用）。</p>
            <p>密码（至少 8 个字符），并再次输入以确认。</p>
          </Section>

          <Section title="注册步骤">
            <ol className="list-decimal ml-6 space-y-1">
              <li>前往<strong style={{ color: '#c9a961' }}>注册 (Sign Up)</strong> 页面。</li>
              <li>输入邮箱和/或电话号码。</li>
              <li>输入推荐代码（如链接中已带有则无需手动输入）。</li>
              <li>输入名字（及姓氏，选填）。</li>
              <li>输入您想要的 10 位 Wellis ID，等待系统确认该 ID 可用。</li>
              <li>设置密码并再次输入确认。</li>
              <li>提交表单。</li>
            </ol>
          </Section>

          <Section title="留意每个字段旁的 ✓ 标记">
            <p>表单中每个字段标题旁都有一个小圆圈（○），一旦该字段有效即变为对勾（✓）。<strong style={{ color: '#c9a961' }}>大多数注册失败都是因为漏看了某个还没变绿的 ✓。</strong>提交前请逐一检查：</p>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong style={{ color: '#c9a961' }}>推荐代码</strong>——系统会自动验证；只有在下方出现绿色的"您正在 XXX 的推荐下注册"提示后，✓ 才会显示。</li>
              <li><strong style={{ color: '#c9a961' }}>Wellis ID</strong>——这是最容易漏掉的一个：输入 ID 后系统<strong style={{ color: '#c9a961' }}>不会自动检查</strong>，您必须点击输入框旁的"检查是否可用"按钮，✓ 才会出现。很多人以为填完 ID 就够了，结果提交时才发现还没验证。</li>
              <li><strong style={{ color: '#c9a961' }}>名字</strong>——只要填写即打勾。</li>
              <li><strong style={{ color: '#c9a961' }}>邮箱或电话</strong>——只要任填一项即打勾。</li>
              <li><strong style={{ color: '#c9a961' }}>密码</strong>——强度必须达到"弱"以上（至少 8 个字符，建议混合大小写、数字与符号）。</li>
              <li><strong style={{ color: '#c9a961' }}>确认密码</strong>——必须与密码完全一致才打勾。</li>
            </ul>
          </Section>

          <Section title="注意事项">
            <ul className="list-disc ml-6 space-y-1">
              <li>账户创建后<strong style={{ color: '#c9a961' }}>即时获得批准</strong>，您可以立即使用刚设置的凭证登录，无需等待管理员手动审核。</li>
              <li>如果您提供了邮箱，系统会发送一封验证邮件——建议尽快验证，但这<strong style={{ color: '#c9a961' }}>不会阻止</strong>您立即登录使用账户。</li>
              <li>Wellis ID 一经创建<strong style={{ color: '#c9a961' }}>不可更改</strong>，请谨慎选择。</li>
              <li>没有有效推荐代码将<strong style={{ color: '#c9a961' }}>无法注册</strong>——请先向您的推荐人索取邀请链接或代码。</li>
              <li>密码和确认密码必须完全一致，且至少 8 个字符。</li>
            </ul>
          </Section>
        </>
      ) : (
        <>
          <Section title="Before you start">
            <p>An email address <strong style={{ color: '#c9a961' }}>or</strong> a phone number (at least one is required).</p>
            <p>A valid <strong style={{ color: '#c9a961' }}>referral code</strong> — signup requires a sponsor, so this field is mandatory. If you opened the signup page from an invite link (?ref=...), it's pre-filled automatically.</p>
            <p>Your first name (required) and last name (optional).</p>
            <p>A <strong style={{ color: '#c9a961' }}>Wellis ID</strong> of your choosing — must be exactly 10 alphanumeric characters and not already taken (the page checks availability for you as you type).</p>
            <p>A password (at least 8 characters), entered twice to confirm.</p>
          </Section>

          <Section title="Signup steps">
            <ol className="list-decimal ml-6 space-y-1">
              <li>Go to the <strong style={{ color: '#c9a961' }}>Sign Up</strong> page.</li>
              <li>Enter your email and/or phone number.</li>
              <li>Enter your referral code (skip if it's already pre-filled from a link).</li>
              <li>Enter your first name (and last name, optional).</li>
              <li>Enter your desired 10-character Wellis ID and wait for it to be confirmed available.</li>
              <li>Set a password and re-enter it to confirm.</li>
              <li>Submit the form.</li>
            </ol>
          </Section>

          <Section title="Check the ✓ marks next to each field">
            <p>Every field label on the form has a small circle (○) next to it that turns into a checkmark (✓) once that field is valid. <strong style={{ color: '#c9a961' }}>Most failed signups happen because a ✓ was missed.</strong> Before submitting, check each one:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong style={{ color: '#c9a961' }}>Referral code</strong> — validated automatically; the ✓ only appears once the green "Signing up under [name]" confirmation shows below the field.</li>
              <li><strong style={{ color: '#c9a961' }}>Wellis ID</strong> — this is the one people miss most: typing your ID does <strong style={{ color: '#c9a961' }}>not</strong> check it automatically. You must click the "Check Uniqueness" button next to the field for the ✓ to appear. Many people fill in the ID, assume it's done, and only find out it was never verified when the form rejects it at submit.</li>
              <li><strong style={{ color: '#c9a961' }}>First name</strong> — ticks as soon as anything is typed.</li>
              <li><strong style={{ color: '#c9a961' }}>Email or phone</strong> — ticks once either one has a value.</li>
              <li><strong style={{ color: '#c9a961' }}>Password</strong> — needs at least "Fair" strength (8+ characters; mixing upper/lower case, numbers, and symbols helps).</li>
              <li><strong style={{ color: '#c9a961' }}>Confirm password</strong> — only ticks when it exactly matches the password field.</li>
            </ul>
          </Section>

          <Section title="Things to note">
            <ul className="list-disc ml-6 space-y-1">
              <li>Your account is <strong style={{ color: '#c9a961' }}>approved instantly</strong> — you can log in right away with the credentials you just set, no manual admin review needed.</li>
              <li>If you provided an email, a verification email is sent — verifying it is recommended, but it does <strong style={{ color: '#c9a961' }}>not</strong> block you from logging in and using your account immediately.</li>
              <li>Your Wellis ID <strong style={{ color: '#c9a961' }}>cannot be changed</strong> once created, so choose carefully.</li>
              <li>You <strong style={{ color: '#c9a961' }}>cannot sign up without</strong> a valid referral code — ask your sponsor for their invite link or code first.</li>
              <li>Password and confirm-password must match exactly and be at least 8 characters.</li>
            </ul>
          </Section>
        </>
      )}
      </>
      )}

      {topic === 'placement' && (
      <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-headline-md font-bold" style={{ color: '#c9a961' }}>
          {isZh ? '如何在族谱中安置会员' : 'How to Place Members in Genealogy'}
        </h2>
        <button
          onClick={() => window.print()}
          className="no-print text-body-sm font-medium px-4 py-2 rounded border hover:opacity-80 transition-opacity"
          style={{ color: '#0d2818', backgroundColor: '#c9a961', borderColor: '#c9a961' }}
        >
          {isZh ? '🖨️ 打印本指南' : '🖨️ Print this guide'}
        </button>
      </div>

      {isZh ? (
        <>
          <Section title="开始之前">
            <p>您必须自己已经在族谱树中拥有位置，才能在您名下安置他人。如果您尚未被安置，请联系管理员。</p>
            <p>您只能安置<strong style={{ color: '#c9a961' }}>您本人推荐的会员</strong>。超级管理员可以安置任何符合资格的会员。</p>
            <p>被推荐的会员在满足以下任一条件后即符合安置资格：</p>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong style={{ color: '#c9a961' }}>已验证</strong>（已购买配套）</li>
              <li><strong style={{ color: '#c9a961' }}>已预处理</strong>（管理员已直接授予其积分）</li>
            </ul>
            <p>已经安置在树中其他位置的会员不能被重复安置。</p>
          </Section>

          <Section title="两种安置方式">
            <p className="font-medium" style={{ color: '#c9a961' }}>方式一 — 通过族谱页面</p>
            <ol className="list-decimal ml-6 space-y-1">
              <li>前往<strong style={{ color: '#c9a961' }}>族谱 (Genealogy)</strong> 页面。</li>
              <li>在您自己的节点下找到空位（显示为"+"或空心圆圈）。</li>
              <li>点击空位，选择要填补的<strong style={{ color: '#c9a961' }}>分支 (Leg)</strong>（1、2 或 3）。</li>
              <li>从您符合资格且尚未安置的推荐人列表中选择该会员。</li>
              <li>确认。</li>
            </ol>
            <p className="font-medium mt-4" style={{ color: '#c9a961' }}>方式二 — 通过"安置我的推荐人"页面</p>
            <ol className="list-decimal ml-6 space-y-1">
              <li>前往<strong style={{ color: '#c9a961' }}>安置我的推荐人 (Place My Referrals)</strong> 页面。</li>
              <li>在左侧列表中选择一位标记为"Ready"（或"Pre-processed"）的推荐人。</li>
              <li>在右侧选择您自己节点下的空位（分支 1、2 或 3）。</li>
              <li>点击<strong style={{ color: '#c9a961' }}>安置推荐人</strong>，然后<strong style={{ color: '#c9a961' }}>确认</strong>。</li>
            </ol>
          </Section>

          <Section title="理解「分支 (Leg)」与「层级 (Level)」">
            <p><strong style={{ color: '#c9a961' }}>分支 (Leg)</strong>：该会员位于特定上级节点下的哪一条分支（1、2 或 3）。</p>
            <p><strong style={{ color: '#c9a961' }}>层级 (Level)</strong>：该位置在树中的第几代（您自己的直接下线为第 0 层，之后依次递增）。</p>
          </Section>

          <Section title="注意事项">
            <p>会员本人无法自行撤销安置——如果安置到错误的位置，请联系管理员进行更正。</p>
            <p>如果您的推荐人列表为空，说明目前没有推荐人符合安置资格（尚未验证/预处理），或他们已被安置。</p>
          </Section>
        </>
      ) : (
        <>
          <Section title="Before you start">
            <p>You must already have your own position in the genealogy tree before you can place anyone under you. If you're not yet placed, contact your admin.</p>
            <p>You can only place members <strong style={{ color: '#c9a961' }}>you personally referred</strong>. Superadmin can place any eligible member.</p>
            <p>A referred member becomes eligible for placement once they are either:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong style={{ color: '#c9a961' }}>Verified</strong> (they've purchased a package), or</li>
              <li><strong style={{ color: '#c9a961' }}>Pre-processed</strong> (an admin has granted them credit points directly)</li>
            </ul>
            <p>A member who is already placed elsewhere in the tree cannot be placed again.</p>
          </Section>

          <Section title="Two ways to place a referral">
            <p className="font-medium" style={{ color: '#c9a961' }}>Option 1 — From the Genealogy page</p>
            <ol className="list-decimal ml-6 space-y-1">
              <li>Go to <strong style={{ color: '#c9a961' }}>Genealogy</strong>.</li>
              <li>Find an empty slot under your own node (shown as a "+" / open circle).</li>
              <li>Tap the empty slot and choose which <strong style={{ color: '#c9a961' }}>Leg</strong> (1, 2, or 3) you're filling.</li>
              <li>Pick the referred member from the list of your eligible, unplaced referrals.</li>
              <li>Confirm.</li>
            </ol>
            <p className="font-medium mt-4" style={{ color: '#c9a961' }}>Option 2 — From "Place My Referrals"</p>
            <ol className="list-decimal ml-6 space-y-1">
              <li>Go to <strong style={{ color: '#c9a961' }}>Place My Referrals</strong>.</li>
              <li>On the left, pick one of your referrals marked "Ready" (or "Pre-processed").</li>
              <li>On the right, pick an open slot (Leg 1, 2, or 3) under your own node.</li>
              <li>Click <strong style={{ color: '#c9a961' }}>Place Referral</strong>, then <strong style={{ color: '#c9a961' }}>Confirm</strong>.</li>
            </ol>
          </Section>

          <Section title={'Understanding "Leg" and "Level"'}>
            <p><strong style={{ color: '#c9a961' }}>Leg</strong>: which of the 3 branches (1, 2, or 3) under a specific parent the member sits in.</p>
            <p><strong style={{ color: '#c9a961' }}>Level</strong>: how many generations deep in the tree that position is (your own position is Level 0 for your direct children, then it counts up from there).</p>
          </Section>

          <Section title="Notes">
            <p>Placement cannot be undone by members themselves — if you place someone in the wrong slot, contact an admin to correct it.</p>
            <p>If your referral list is empty, it means none of your referrals are currently eligible (not yet verified/pre-processed) or they're already placed.</p>
          </Section>
        </>
      )}
      </>
      )}
      </div>
    </div>

      {/* Floating quick-switch tool */}
      <div className="no-print fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {floatOpen && (
          <div className="rounded-lg shadow-lg border overflow-hidden mb-1" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            {TOPICS.map((t) => (
              <button
                key={t.id}
                onClick={() => selectTopic(t.id)}
                className="block w-full text-left px-4 py-3 text-body-sm whitespace-nowrap transition-colors"
                style={
                  topic === t.id
                    ? { color: '#0d2818', backgroundColor: '#c9a961' }
                    : { color: '#c9a961', backgroundColor: 'transparent' }
                }
              >
                {t.icon} {isZh ? t.zh : t.en}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={() => setFloatOpen(!floatOpen)}
          className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-2xl hover:opacity-90 transition-opacity"
          style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          aria-label={isZh ? '快速切换指南' : 'Quick-switch guides'}
          title={isZh ? '快速切换指南' : 'Quick-switch guides'}
        >
          {floatOpen ? '✕' : '📚'}
        </button>
      </div>
    </div>
  );
}
